/**
 * Reusable focus-trap / focus-restore behavior for modal-like components.
 *
 * 
 *  - call `this.activateFocusTrap()` right after the modal becomes visible
 *    that is only ever mounted while open),
 *  - call `this.deactivateFocusTrap()` right before/when the modal closes,
 *
 
 */
export default {
	data() {
		return {
			focusTrapLastFocusedElement: null,
			focusTrapFallbackSelector: null,
			focusTrapObserver: null
		};
	},
	computed: {
		focusTrapRef() {
			return "modal";
		},
		focusTrapContentSelector() {
			return ".price, .detail-content, .validity,.savings, .regularPrice, .daysOnly, .bodyEn , .bodyFr, .similar-items  , .similar-item-image ,  .similar-item-price,.similar-item-savings, .similar-item-validity" ;
		}
	},
	beforeDestroy() {
		this.deactivateFocusTrap();
	},
	methods: {
		getFocusableElements() {
			const container = this.$refs[this.focusTrapRef];
			if (!container) return [];
			return Array.from(container.querySelectorAll(
				'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
			)).filter(el => el.offsetParent !== null);
		},

		// Makes static content blocks (price, description, ...) part of the
		// tab order, matching whatever elements in the template already do
		// this by hand (e.g. `.price`), without needing per-template edits.
		ensureContentFocusable() {
			const container = this.$refs[this.focusTrapRef];
			if (!container || !this.focusTrapContentSelector) return;
			container.querySelectorAll(this.focusTrapContentSelector).forEach(el => {
				if (!el.hasAttribute("tabindex")) {
					el.setAttribute("tabindex", "0");
				}
			});
		},

		// `ensureContentFocusable` only stamps the elements that exist when the
		// modal opens. Anything Vue renders later - paging the similar-items
		// carousel swaps both cards, toggling add/remove re-renders the button -
		// arrives without a tabindex and silently drops out of the tab order.
		// Watch for inserted nodes and re-stamp them.
		startContentObserver() {
			const container = this.$refs[this.focusTrapRef];
			if (!container || !this.focusTrapContentSelector) return;
			if (typeof MutationObserver === "undefined") return;

			this.stopContentObserver();
			// childList only: `ensureContentFocusable` sets attributes, so observing
			// attributes here would retrigger this callback on its own writes.
			this.focusTrapObserver = new MutationObserver(mutations => {
				if (mutations.some(m => m.addedNodes.length)) {
					this.ensureContentFocusable();
				}
			});
			this.focusTrapObserver.observe(container, {childList: true, subtree: true});
		},

		stopContentObserver() {
			this.focusTrapObserver?.disconnect();
			this.focusTrapObserver = null;
		},

		onFocusTrapKeydown(e) {
			if (e.key === "Escape") {
				e.stopPropagation();
				if (typeof this.onFocusTrapEscape === "function") {
					this.onFocusTrapEscape();
				}
				return;
			}
			if (e.key !== "Tab") return;

			const focusable = this.getFocusableElements();
			if (!focusable.length) return;
			const first = focusable[0];
			const last = focusable[focusable.length - 1];
			const container = this.$refs[this.focusTrapRef];

			// Focus somehow ended up outside the modal - send this Tab press
			// back into it instead of letting it fall through to the page behind it.
			const isInsideModal = container && container.contains(document.activeElement);
			if (!isInsideModal) {
				e.preventDefault();
				(e.shiftKey ? last : first).focus();
				return;
			}

			if (e.shiftKey && document.activeElement === first) {
				e.preventDefault();
				last.focus();
			} else if (!e.shiftKey && document.activeElement === last) {
				e.preventDefault();
				first.focus();
			}
		},

		/**
		 * Call when the modal opens: remembers what was focused, moves focus
		 * into the modal, and starts trapping Tab/Escape.
		 * @param {string} [fallbackSelector] CSS selector used to restore focus
		 *   if the original trigger element no longer exists when the modal closes.
		 */
		activateFocusTrap(fallbackSelector) {
			this.focusTrapLastFocusedElement = document.activeElement;
			this.focusTrapFallbackSelector = fallbackSelector || null;
			document.addEventListener("keydown", this.onFocusTrapKeydown);
			this.$nextTick(() => {
				const container = this.$refs[this.focusTrapRef];
				if (!container) return;
				this.ensureContentFocusable();
				this.startContentObserver();
				// Focus the container itself (it carries tabindex="-1") rather than the
				// first control, so screen readers announce the dialog by its accessible
				// name and read its content instead of jumping straight to "Close PDP".
				if (container.hasAttribute("tabindex")) {
					container.focus();
				} else {
					this.getFocusableElements()[0]?.focus();
				}
			});
		},

		/**
		 * Call when the modal closes: stops trapping and restores focus to
		 * whatever triggered the modal (or the fallback selector if that
		 * element is gone).
		 */
		deactivateFocusTrap() {
			document.removeEventListener("keydown", this.onFocusTrapKeydown);
			this.stopContentObserver();
			const restoreTo = (this.focusTrapLastFocusedElement && document.contains(this.focusTrapLastFocusedElement))
				? this.focusTrapLastFocusedElement
				: (this.focusTrapFallbackSelector && document.querySelector(this.focusTrapFallbackSelector));
			restoreTo?.focus();
			this.focusTrapLastFocusedElement = null;
		}
	}
};
